/**
 * `PUT /api/cart` — mutate a single `cart_items` row.
 *
 * Two write operations share the same wire body: quantity change and shipping
 * method change. The route reads `qty` (number) and `shippingId` (string)
 * independently, applying whichever is present. Wire-level shape allows both
 * to coexist, but every existing caller (`cart-provider.tsx`) sends exactly
 * one. We model that as two named request interfaces — one per builder
 * (`updateCartQuantity`, `updateCartShippingMethod`) — and pin each with its
 * own schema. The shared identity fields (`id`, `userId?`, `guestId?`) are
 * factored out so the two schemas stay aligned.
 *
 * Identity: `userId` re-validated against session (mismatch → 403); guests
 * pass `guestId`. XOR at the call-site, not enforced wire-side — the route
 * tolerates either.
 *
 * Response: `{ success: true }` on the happy path, `{ error: string }` on
 * non-2xx — not parsed by current consumers (write-path is fire-and-acknowledge),
 * so no response schema lives here.
 *
 * @see app/api/cart/route.ts PUT — handler
 * @see lib/cart/update-cart-quantity.ts — quantity builder
 * @see lib/cart/update-cart-shipping.ts — shipping-method builder
 */

import { z } from "zod";
import { pinned } from "./pin";

interface CartUpdateIdentity {
  /** `cart_items.id` to mutate. */
  id: string;
  /** Authenticated user id. XOR with `guestId` at the call-site. */
  userId?: string | null;
  /** Guest cart id. XOR with `userId` at the call-site. */
  guestId?: string | null;
}

export interface CartUpdateQuantityRequest extends CartUpdateIdentity {
  /** New buyer-chosen quantity; route caps at listing stock. */
  qty: number;
}

export interface CartUpdateShippingRequest extends CartUpdateIdentity {
  /** `shipping_methods.id` to set on the row. */
  shippingId: string;
}

const IdentitySchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1).nullable().optional(),
  guestId: z.string().min(1).nullable().optional(),
});

export const CartUpdateQuantityRequestSchema = pinned<CartUpdateQuantityRequest>()(
  IdentitySchema.extend({
    qty: z.number().int().min(1),
  }),
);

export const CartUpdateShippingRequestSchema = pinned<CartUpdateShippingRequest>()(
  IdentitySchema.extend({
    shippingId: z.string().min(1),
  }),
);
